/**
 * Import measurement
 *
 * Pure functions that turn per-question review records into the POC metrics: time
 * from upload to published against a manual baseline, fields accepted unchanged by
 * field and confidence band, correction rate per field, and the expert rejection
 * rate on staged questions. Every record carries the ImportJob, the paper and the
 * commit hash of the prompt that produced the inference, so any metric can be
 * sliced by any of the three. No I/O.
 * @format
 */

import { type ConfidenceBandName, CONFIDENCE_BANDS, confidenceBandFor } from "./classification/classification-scoring";

/** Question fields the pipeline proposes and a reviewer may correct. */
export const IMPORT_REVIEW_FIELDS = ["theme", "topic", "subTopic", "marks", "text", "assessmentObjectives"] as const;
export type ImportReviewField = (typeof IMPORT_REVIEW_FIELDS)[number];

/** What the pipeline proposed for one field and what the reviewer left in place. */
export interface ImportFieldReview {
	field: ImportReviewField;
	/** Model confidence 0 to 1. Absent for deterministic fields, which are banded `unscored`. */
	confidence?: number;
	/** The pipeline's value, serialised for comparison */
	proposed: string;
	/** The value after review */
	final: string;
}

/** An expert sending a staged question back to draft. */
export interface ImportRejection {
	at: string;
	reason: string;
	rejectedBy?: string;
}

/**
 * Review provenance of one question from one ImportJob.
 */
export interface ImportQuestionReview {
	questionId: string;
	importJobId: string;
	paperId: string;
	/**
	 * Commit hash of the prompt that produced the inference. Locks the metrics to one
	 * prompt version so accuracy changes can be traced to prompt changes.
	 */
	promptCommit: string;
	/** When the job was created (upload) */
	uploadedAt: string;
	/** When the uploader moved the question to `staged` */
	stagedAt?: string;
	/** When the expert published the question */
	publishedAt?: string;
	fields: ImportFieldReview[];
	/** Every expert rejection, in order. A question may be rejected and staged again. */
	rejections: ImportRejection[];
}

export type MetricBand = ConfidenceBandName | "unscored";

export const METRIC_BANDS: readonly MetricBand[] = [...CONFIDENCE_BANDS.map((b) => b.name), "unscored"];

export interface FieldRate {
	total: number;
	unchanged: number;
	corrected: number;
	/** Share of `total` accepted unchanged, 0 when `total` is 0 */
	acceptedUnchangedRate: number;
	/** Share of `total` corrected, 0 when `total` is 0 */
	correctionRate: number;
}

export interface TimeToPublishMetrics {
	/** Published questions the figures are computed from */
	publishedCount: number;
	/** Per question, upload to published, in milliseconds */
	durationsMs: number[];
	medianMs?: number;
	meanMs?: number;
	/** Manual authoring time per question, when supplied */
	manualBaselineMsPerQuestion?: number;
	/** Median divided by the baseline: below 1 is faster than manual */
	medianVsBaseline?: number;
}

export interface RejectionMetrics {
	/** Questions that reached `staged` at least once */
	stagedCount: number;
	/** Questions an expert rejected at least once */
	rejectedQuestionCount: number;
	/** Total rejection events */
	rejectionCount: number;
	/** rejectedQuestionCount / stagedCount, 0 when nothing was staged */
	rejectionRate: number;
}

export interface ImportMetrics {
	questionCount: number;
	timeToPublish: TimeToPublishMetrics;
	byField: Partial<Record<ImportReviewField, FieldRate>>;
	byFieldAndBand: Partial<Record<ImportReviewField, Record<MetricBand, FieldRate>>>;
	rejections: RejectionMetrics;
}

export interface ImportMetricsOptions {
	/** Time a human takes to author one question manually, in milliseconds */
	manualBaselineMsPerQuestion?: number;
}

export type ImportMetricsGroupKey = "importJobId" | "paperId" | "promptCommit";

const rate = (total: number, unchanged: number): FieldRate => ({
	total,
	unchanged,
	corrected: total - unchanged,
	acceptedUnchangedRate: total === 0 ? 0 : unchanged / total,
	correctionRate: total === 0 ? 0 : (total - unchanged) / total,
});

/** Band for a field: its own confidence band, or `unscored` when it has none. */
export const metricBandFor = (confidence: number | undefined): MetricBand =>
	confidence === undefined ? "unscored" : confidenceBandFor(confidence);

const median = (values: number[]): number | undefined => {
	if (values.length === 0) {
		return undefined;
	}
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Upload to published for one question, or undefined when it is not published or the times are invalid. */
export const timeToPublishMs = (review: ImportQuestionReview): number | undefined => {
	if (!review.publishedAt) {
		return undefined;
	}
	const ms = Date.parse(review.publishedAt) - Date.parse(review.uploadedAt);
	return Number.isFinite(ms) && ms >= 0 ? ms : undefined;
};

/** Compute the POC metrics for a set of question reviews. */
export function computeImportMetrics(
	reviews: ImportQuestionReview[],
	options: ImportMetricsOptions = {},
): ImportMetrics {
	const durationsMs = reviews.map(timeToPublishMs).filter((ms): ms is number => ms !== undefined);
	const medianMs = median(durationsMs);
	const meanMs = durationsMs.length ? durationsMs.reduce((a, b) => a + b, 0) / durationsMs.length : undefined;
	const baseline = options.manualBaselineMsPerQuestion;

	const fieldTotals = new Map<ImportReviewField, { total: number; unchanged: number }>();
	const bandTotals = new Map<ImportReviewField, Map<MetricBand, { total: number; unchanged: number }>>();
	const bump = (target: { total: number; unchanged: number }, isUnchanged: boolean) => {
		target.total += 1;
		if (isUnchanged) {
			target.unchanged += 1;
		}
	};

	for (const review of reviews) {
		for (const f of review.fields) {
			const isUnchanged = f.proposed === f.final;
			const overall = fieldTotals.get(f.field) ?? { total: 0, unchanged: 0 };
			bump(overall, isUnchanged);
			fieldTotals.set(f.field, overall);

			const bands = bandTotals.get(f.field) ?? new Map<MetricBand, { total: number; unchanged: number }>();
			const band = metricBandFor(f.confidence);
			const entry = bands.get(band) ?? { total: 0, unchanged: 0 };
			bump(entry, isUnchanged);
			bands.set(band, entry);
			bandTotals.set(f.field, bands);
		}
	}

	const byField: ImportMetrics["byField"] = {};
	const byFieldAndBand: ImportMetrics["byFieldAndBand"] = {};
	for (const [field, t] of fieldTotals) {
		byField[field] = rate(t.total, t.unchanged);
		const bands = bandTotals.get(field);
		byFieldAndBand[field] = Object.fromEntries(
			METRIC_BANDS.map((b) => [b, rate(bands?.get(b)?.total ?? 0, bands?.get(b)?.unchanged ?? 0)]),
		) as Record<MetricBand, FieldRate>;
	}

	const staged = reviews.filter((r) => r.stagedAt || r.publishedAt || r.rejections.length > 0);
	const rejected = reviews.filter((r) => r.rejections.length > 0);

	return {
		questionCount: reviews.length,
		timeToPublish: {
			publishedCount: durationsMs.length,
			durationsMs,
			medianMs,
			meanMs,
			manualBaselineMsPerQuestion: baseline,
			medianVsBaseline: medianMs !== undefined && baseline ? medianMs / baseline : undefined,
		},
		byField,
		byFieldAndBand,
		rejections: {
			stagedCount: staged.length,
			rejectedQuestionCount: rejected.length,
			rejectionCount: rejected.reduce((n, r) => n + r.rejections.length, 0),
			rejectionRate: staged.length === 0 ? 0 : rejected.length / staged.length,
		},
	};
}

/** Compute metrics separately per ImportJob, paper or prompt commit. */
export function computeImportMetricsBy(
	reviews: ImportQuestionReview[],
	key: ImportMetricsGroupKey,
	options: ImportMetricsOptions = {},
): Record<string, ImportMetrics> {
	const groups = new Map<string, ImportQuestionReview[]>();
	for (const r of reviews) {
		groups.set(r[key], [...(groups.get(r[key]) ?? []), r]);
	}
	return Object.fromEntries([...groups].map(([k, rs]) => [k, computeImportMetrics(rs, options)]));
}

const percent = (value: number): string => `${Math.round(value * 1000) / 10}%`;

const minutes = (ms: number | undefined): string => (ms === undefined ? "n/a" : `${Math.round(ms / 60_000)} min`);

/**
 * The four POC measurements, as plain text a write-up can include unchanged.
 * The manual baseline is whatever the caller passed to {@link computeImportMetrics}.
 */
export function formatImportMetricsReport(metrics: ImportMetrics, title = "PDF import POC run"): string {
	const time = metrics.timeToPublish;
	const ratio = time.medianVsBaseline === undefined ? "n/a" : String(Math.round(time.medianVsBaseline * 100) / 100);
	const fieldLines = IMPORT_REVIEW_FIELDS.map((field) => {
		const rate = metrics.byField[field];
		if (!rate || rate.total === 0) {
			return `- ${field}: no reviews`;
		}
		return `- ${field}: ${rate.unchanged} of ${rate.total} unchanged (${percent(rate.acceptedUnchangedRate)})`;
	});
	const bandLines = IMPORT_REVIEW_FIELDS.flatMap((field) => {
		const bands = metrics.byFieldAndBand[field];
		if (!bands) {
			return [];
		}
		return METRIC_BANDS.filter((band) => (bands[band]?.total ?? 0) > 0).map((band) => {
			const rate = bands[band];
			return `- ${field} / ${band}: ${rate.unchanged} of ${rate.total} unchanged (${percent(rate.acceptedUnchangedRate)})`;
		});
	});
	const correctionLines = IMPORT_REVIEW_FIELDS.map((field) => {
		const rate = metrics.byField[field];
		if (!rate || rate.total === 0) {
			return `- ${field}: no reviews`;
		}
		return `- ${field}: ${rate.corrected} of ${rate.total} corrected (${percent(rate.correctionRate)})`;
	});
	const rejections = metrics.rejections;

	return [
		`# ${title}`,
		"",
		"## Time from upload to published",
		`Published questions: ${time.publishedCount}`,
		`Median: ${minutes(time.medianMs)}`,
		`Mean: ${minutes(time.meanMs)}`,
		`Manual baseline per question: ${minutes(time.manualBaselineMsPerQuestion)}`,
		`Median divided by baseline: ${ratio}`,
		"",
		"## Fields accepted unchanged",
		...fieldLines,
		"",
		"## Fields accepted unchanged by confidence band",
		...(bandLines.length > 0 ? bandLines : ["- none"]),
		"",
		"## Correction rate per field",
		...correctionLines,
		"",
		"## Expert rejection rate",
		`Staged questions: ${rejections.stagedCount}`,
		`Rejected questions: ${rejections.rejectedQuestionCount}`,
		`Rejection events: ${rejections.rejectionCount}`,
		`Rejection rate: ${percent(rejections.rejectionRate)}`,
	].join("\n");
}
